import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  NotebookPen,
  GraduationCap,
  ScrollText,
  Headphones,
  Video,
  Package,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/store";

export interface ProductCardData {
  id: string;
  slug: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  price_cents: number;
  product_type: string;
  category?: string | null;
  thumbnail_url?: string | null;
  pages?: number | null;
  duration_minutes?: number | null;
}

const TYPE_ICON: Record<string, LucideIcon> = {
  teaching: BookOpen,
  workbook: NotebookPen,
  course: GraduationCap,
  "study-guide": ScrollText,
  audio: Headphones,
  video: Video,
  bundle: Package,
};

function typeLabel(type: string) {
  return type
    .split("-")
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(" ");
}

export function ProductCard({
  product,
  className,
}: {
  product: ProductCardData;
  className?: string;
}) {
  const Icon = TYPE_ICON[product.product_type] ?? BookOpen;
  const meta =
    product.pages != null
      ? `${product.pages}p`
      : product.duration_minutes != null
        ? `${product.duration_minutes}min`
        : null;

  return (
    <Link
      to="/store/products/$slug"
      params={{ slug: product.slug }}
      className={cn(
        "group flex flex-col overflow-hidden rounded-sm border border-bronze-600/15 bg-white",
        "transition-all duration-300 hover:-translate-y-0.5 hover:border-bronze-600/50",
        "hover:shadow-[0_18px_50px_-18px_rgba(168,139,92,0.35)]",
        className,
      )}
    >
      <div className="relative">
        {product.thumbnail_url ? (
          <img
            src={product.thumbnail_url}
            alt={product.title}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        ) : (
          <div className="flex aspect-[4/3] w-full items-center justify-center bg-navy-800 text-cream-50/40">
            <Icon className="h-14 w-14" strokeWidth={1.2} aria-hidden />
          </div>
        )}

        <span
          className="absolute left-3 top-3 inline-flex items-center gap-1 bg-bronze-400 px-2 py-1 font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-900"
          style={{ borderRadius: "2px" }}
        >
          <Icon className="h-3 w-3" strokeWidth={2} aria-hidden />
          {typeLabel(product.product_type)}
        </span>

      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-serif text-[18px] leading-snug text-navy-800">
          {product.title}
        </h3>
        {product.description ? (
          <p
            className="mt-2 font-sans text-[13px] leading-relaxed text-charcoal-500"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {product.description.replace(/[#*_>`]/g, "").trim()}
          </p>
        ) : product.subtitle ? (
          <p className="mt-2 font-sans text-[13px] leading-relaxed text-charcoal-500">
            {product.subtitle}
          </p>
        ) : null}

        <div className="mt-auto flex items-baseline justify-between pt-4">
          <span className="font-serif text-[20px] text-bronze-600">
            {formatPrice(product.price_cents)}
          </span>
          {meta && (
            <span className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
              {meta}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default ProductCard;